import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, Controller, Control } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Sparkles, Trash2, Link2Off, ImageIcon, Upload, X, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@omit/react-confirm-dialog";
import { generateLocationSummary } from "@/network/network";
import { useIntegrationErrorReporter } from "@/integrations";
import FilePreview from "@/modern/components/file-preview";
import { filterLocationPhotoFiles, useLocationPhotos } from "@/react/hooks/entityHooks/useLocationPhotos";

import { Input } from "@/modern/components/ui/input";
import { Textarea } from "@/modern/components/ui/textarea";
import { Button } from "@/modern/components/ui/button";
import { ButtonGroup } from "@/modern/components/ui/button-group";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { EditorSection } from "../../general/components/EditorSection";
import { EditorFormField } from "../../general/components/EditorFormField";
import { AddressAutoComplete } from "../components/AddressAutoComplete";
import { LocationReadinessBadge } from "../components/LocationReadinessBadge";
import { Location } from "@/bryntum-lib/models/Location";
import { locationSchema, type LocationFormData } from "@/react/schema/locationSchema";
import { type GooglePlaceDetails } from "@/react/hooks/entityHooks/useSwivuLocation";
import { EntityRecord } from "@/types/entity";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { cn } from "@/modern/utils";
import { GRANTS } from "@/other/constants/PrivilegeTypes.jsx";

// ─── LocationEditorPhotos ─────────────────────────────────────────────────────

interface LocationEditorPhotosProps {
  locationRecord: Location | null | undefined;
  boardId: number;
  stagedFiles: File[];
  photosToRemove: Set<string | number>;
  onStageFiles: (files: FileList | File[]) => void;
  onRemoveStaged: (index: number) => void;
  onMarkForRemoval: (id: string | number) => void;
  isSaving: boolean;
  isReadOnly: boolean;
}

const LocationEditorPhotos = ({
  locationRecord,
  boardId,
  stagedFiles,
  photosToRemove,
  onStageFiles,
  onRemoveStaged,
  onMarkForRemoval,
  isSaving,
  isReadOnly,
}: LocationEditorPhotosProps) => {
  const { photos } = useLocationPhotos(locationRecord, boardId);
  const uploadRef = useRef<HTMLInputElement>(null);

  // Exclude photos that have been staged for removal
  const visibleExisting = useMemo(
    () => photos.filter((f) => !photosToRemove.has(f.id)),
    [photos, photosToRemove]
  );

  // Local object URLs for staged files — revoked on cleanup
  const stagedPreviews = useMemo(
    () => stagedFiles.map((f) => ({ file: f, url: URL.createObjectURL(f) })),
    [stagedFiles]
  );
  useEffect(() => {
    return () => stagedPreviews.forEach(({ url }) => URL.revokeObjectURL(url));
  }, [stagedPreviews]);

  const hasAny = visibleExisting.length > 0 || stagedFiles.length > 0;

  return (
    <EditorSection withDivider>
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <p className="text-sm font-medium text-foreground">Photos</p>
          <p className="text-xs text-muted-foreground/60">
            Site photos for this location. Changes are applied when you save.
          </p>
        </div>
        <button
          type="button"
          onClick={() => uploadRef.current?.click()}
          disabled={isSaving || isReadOnly}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors disabled:opacity-40"
        >
          <Upload className="size-3" />
          Add photos
        </button>
        <input
          ref={uploadRef}
          type="file"
          className="hidden"
          multiple
          accept="image/*"
          disabled={isSaving || isReadOnly}
          onChange={(e) => {
            if (e.target.files?.length) {
              onStageFiles(e.target.files);
              e.target.value = "";
            }
          }}
        />
      </div>

      {!hasAny ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-muted-foreground/30 bg-muted/40 py-6 text-center">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ImageIcon className="size-4" />
          </span>
          <div className="space-y-0.5">
            <p className="text-xs font-medium text-foreground">No site photos yet</p>
            <p className="text-xs text-muted-foreground">Add photos — they'll be uploaded when you save.</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-3">
          {/* Existing persisted photos */}
          {visibleExisting.map((file) => (
            <div key={file.id} className="group relative aspect-square overflow-hidden rounded-md border border-border bg-muted">
              <FilePreview
                file={file}
                src={file.url || file.thumbnailUrl || undefined}
                className="w-full h-full object-cover"
                showName={false}
                openFileOnClick={false}
                disableLoadingState
                isThumbnail
              />
              <button
                type="button"
                onClick={() => onMarkForRemoval(file.id)}
                disabled={isSaving || isReadOnly}
                className="absolute top-1 right-1 flex size-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity shadow"
                aria-label="Remove photo"
              >
                <X className="size-3" />
              </button>
            </div>
          ))}

          {/* Staged (pending upload) photos */}
          {stagedPreviews.map(({ file, url }, idx) => (
            <div key={url} className="group relative aspect-square overflow-hidden rounded-md border border-primary/50 bg-muted">
              <img src={url} alt={file.name} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => onRemoveStaged(idx)}
                disabled={isSaving || isReadOnly}
                className="absolute top-1 right-1 flex size-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity shadow"
                aria-label="Remove staged photo"
              >
                <X className="size-3" />
              </button>
            </div>
          ))}

          {/* Add-more cell */}
          <button
            type="button"
            onClick={() => uploadRef.current?.click()}
            disabled={isSaving || isReadOnly}
            className="aspect-square rounded-md border border-dashed border-muted-foreground/40 bg-muted/40 flex flex-col items-center justify-center gap-1 text-muted-foreground hover:bg-muted/70 transition-colors disabled:opacity-40"
          >
            <Plus className="size-4" />
            <span className="text-[10px]">Add</span>
          </button>
        </div>
      )}
    </EditorSection>
  );
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface LocationEditorProps {
  record: {
    locationRecord?: Location | null;
    entityRecord?: EntityRecord;
    disableUnlinkUntilPersisted?: boolean;
  };
  onClose: () => void;
  editorInstance?: {
    className?: string;
    [key: string]: unknown;
  };
}

// ─── Default values from model ────────────────────────────────────────────────

function toDefaults(loc: Location): LocationFormData {
  return {
    streetAddress: loc.streetAddress ?? "",
    city: loc.city ?? "",
    stateOrRegion: loc.stateOrRegion ?? "",
    postalCode: loc.postalCode ?? "",
    country: loc.country ?? "",
    latitude: loc.latitude ?? null,
    longitude: loc.longitude ?? null,
    propertyName: loc.propertyName ?? "",
    buildingName: loc.buildingName ?? "",
    floorOrLevel: loc.floorOrLevel ?? "",
    elevation: loc.elevation ?? "",
    workZoneName: loc.workZoneName ?? "",
    primaryEntryRoute: loc.primaryEntryRoute ?? "",
    stagingArea: loc.stagingArea ?? "",
    startHereNote: loc.startHereNote ?? "",
    locationSummary: loc.locationSummary ?? "",
    workforceHandoffNote: loc.workforceHandoffNote ?? "",
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

// Empty defaults for when no location record exists yet
const EMPTY_DEFAULTS: LocationFormData = {
  streetAddress: "", city: "", stateOrRegion: "", postalCode: "", country: "",
  latitude: null, longitude: null, propertyName: "", buildingName: "",
  floorOrLevel: "", elevation: "", workZoneName: "",
  primaryEntryRoute: "", stagingArea: "", startHereNote: "",
  locationSummary: "", workforceHandoffNote: "",
};

// ─── AiTextareaField ─────────────────────────────────────────────────────────

interface AiTextareaFieldProps {
  name: keyof LocationFormData;
  label: string;
  placeholder: string;
  control: Control<LocationFormData>;
  isSaving: boolean;
  ringClass: (key: keyof LocationFormData) => string | undefined;
}

const AiTextareaField = ({
  name,
  label,
  placeholder,
  control,
  isSaving,
  ringClass,
}: AiTextareaFieldProps) => {
  return (
    <EditorFormField label={label} htmlFor={name}>
      <div className="relative">
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <Textarea
              id={name}
              placeholder={placeholder}
              autoComplete="off"
              rows={3}
              disabled={isSaving}
              {...field}
              value={field.value as string ?? ""}
              className={cn("resize-y !text-base min-h-[4.5rem]", ringClass(name))}
            />
          )}
        />
      </div>
    </EditorFormField>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

export const LocationEditor = ({ record, onClose, editorInstance }: LocationEditorProps) => {
  const { locationRecord, entityRecord, disableUnlinkUntilPersisted } = record;
  const { project, selectedBoard, user } = useSwivuBryntumContext();
  const { reportError } = useIntegrationErrorReporter();
  const confirm = useConfirm();

  const [isSaving, setIsSaving] = React.useState(false);
  const [highlightedFields, setHighlightedFields] = React.useState<Set<string>>(new Set());
  const isReadOnly = !user?.can?.(GRANTS.grant_four);

  // ─── Photo staging (deferred until save) ─────────────────────────────────
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [photosToRemove, setPhotosToRemove] = useState<Set<string | number>>(new Set());

  const handleStageFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const imageFiles = filterLocationPhotoFiles(fileArray);

    if (imageFiles.length !== fileArray.length) {
      toast.error("Only image files can be uploaded as location photos.");
    }
    if (imageFiles.length === 0) return;

    setStagedFiles((prev) => [...prev, ...imageFiles]);
  }, []);

  const handleRemoveStaged = useCallback((index: number) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleMarkForRemoval = useCallback((id: string | number) => {
    setPhotosToRemove((prev) => new Set(prev).add(id));
  }, []);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * The Location record being edited during this session.
   * Initialized from locationRecord so that edits to an existing location
   * always update that record rather than creating a new one.
   * null  = no existing record; save will create a new one.
   * non-null = update this specific record on save.
   */
  const [activeLocation, setActiveLocation] = React.useState<Location | null>(locationRecord ?? null);

  const boardId = selectedBoard?.id ?? 0;
  const { uploadPhotos, removePhoto } = useLocationPhotos(activeLocation, boardId);

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    watch,
    reset,
    getValues,
    formState: { errors, isDirty },
  } = useForm<LocationFormData>({
    resolver: zodResolver(locationSchema),
    defaultValues: locationRecord ? toDefaults(locationRecord) : EMPTY_DEFAULTS,
  });

  const streetAddressValue = watch("streetAddress");
  const hasStreetAddress = streetAddressValue.trim().length > 0;

  // ─── AI Summary generation (single button) ───────────────────────────────
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [didJustGenerate, setDidJustGenerate] = useState(false);

  // ─── Highlight helper ─────────────────────────────────────────────────────

  const flashHighlight = useCallback((keys: (keyof LocationFormData)[]) => {
    setHighlightedFields(new Set(keys));
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => setHighlightedFields(new Set()), 2200);
  }, []);

  const ringClass = (key: keyof LocationFormData) =>
    highlightedFields.has(key) ? "ring-2 ring-primary/50 transition-all duration-300" : undefined;

  // ─── AddressAutoComplete callbacks ────────────────────────────────────────

  /**
   * User selected an already-saved Location from the store.
   * Confirm they intend to edit it, then populate the form.
   */
  const handleSelectSavedLocation = useCallback(
    async (loc: Location) => {
      const locationName = loc.propertyName || loc.streetAddress || "this location";
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const usersStore = project.boardUsersStore as any;
      const currentUser = usersStore.swivuCurrentUser;
      const boardUser = usersStore.find((u: any) => u.userId === currentUser?.id);
      const userName: string = boardUser?.name ?? "there";
      const confirmed = await confirm({
        title: "Heads Up",
        description: `Hey ${userName}. You just selected "${locationName}". Watch out. When you click Save, changes will be saved to this existing record.`,
        confirmText: "OK",
        cancelButton: null,
        alertDialogOverlay: { className: "z-[--swivu-bryntum-z-index]" },
        alertDialogContent: { className: "z-[--swivu-bryntum-z-index]" },
      });

      if (!confirmed) return;

      // Populate form with the selected location's data
      reset(toDefaults(loc));
      setActiveLocation(loc);

      // Highlight all populated fields
      const allFields = Object.keys(toDefaults(loc)) as (keyof LocationFormData)[];
      flashHighlight(allFields);
    },
    [confirm, reset, flashHighlight]
  );

  /**
   * User selected a Google Places suggestion.
   * The component already resolved the place details before calling this.
   */
  const handleSelectGooglePlace = useCallback(
    (details: GooglePlaceDetails) => {
      const updates: Partial<Record<keyof LocationFormData, unknown>> = {
        // Use the street-only portion from address components; fall back to full formatted address
        streetAddress: details.streetAddress ?? details.formattedAddress,
        city: details.city ?? "",
        stateOrRegion: details.stateOrRegion ?? "",
        postalCode: details.postalCode ?? "",
        country: details.country ?? "",
        latitude: details.lat || null,
        longitude: details.lon || null,
        // Only pre-fill propertyName when Google actually identifies a named place;
        // omitting it entirely avoids a spurious highlight when the value is empty.
        ...(details.propertyName ? { propertyName: details.propertyName } : {}),
      };

      (Object.entries(updates) as [keyof LocationFormData, unknown][]).forEach(
        ([key, val]) => setValue(key, val as never, { shouldDirty: true })
      );

      flashHighlight(Object.keys(updates) as (keyof LocationFormData)[]);
    },
    [setValue, flashHighlight]
  );

  /**
   * User picked the "Set <query>" fallback (no Google/store results found).
   * Sets the street address field only — all other fields are left untouched.
   */
  const handleSetManual = useCallback(
    (address: string) => {
      setValue("streetAddress", address, { shouldDirty: true });
      flashHighlight(["streetAddress"]);
    },
    [setValue, flashHighlight]
  );

  // ─── Delete & Unlink ──────────────────────────────────────────────────────

  const locationToActOn = activeLocation ?? locationRecord;
  const isPersistedLocation = !!locationToActOn && !locationToActOn.isPhantom;
  const isLinked = !!entityRecord && (entityRecord.locationId ?? null) !== null;

  const handleDelete = async () => {
    if (isReadOnly) return;
    if (!locationToActOn) return;
    const confirmed = await confirm({
      title: "Delete location?",
      description: "This will permanently delete the location record. This cannot be undone.",
      alertDialogOverlay: { className: "z-[--swivu-bryntum-z-index]" },
      alertDialogContent: { className: "z-[--swivu-bryntum-z-index]" },
      customActions: ({ confirm, cancel }: { confirm: () => void; cancel: () => void }) => (
        <div className="flex w-full justify-end gap-3">
          <Button variant="ghost" onClick={cancel} size="sm">
            Cancel
          </Button>
          <Button variant="destructive" onClick={confirm} size="sm">
            Delete
          </Button>
        </div>
      ),
    });
    if (!confirmed) return;
    // Close immediately — sync in background
    onClose();
    try {
      if (entityRecord) entityRecord.set("locationId", null);
      project.locationStore.remove(locationToActOn.id);
      await project.sync();
      toast.success("Location deleted");
    } catch {
      toast.error("Failed to delete location. Please try again.");
    }
  };

  const handleUnlink = async () => {
    if (isReadOnly) return;
    if (!entityRecord) return;
    // Close immediately — sync in background
    onClose();
    entityRecord.set("locationId", null);
    try {
      await project.sync();
      toast.success("Location removed from task");
    } catch {
      toast.error("Failed to remove location. Please try again.");
    }
  };

  // ─── Save ─────────────────────────────────────────────────────────────────

  const onSubmit = async (data: LocationFormData) => {
    try {
      setIsSaving(true);

      let savedRecord: Location;

      if (activeLocation) {
        // Update the existing record (persisted or phantom)
        activeLocation.set(data as unknown as Record<string, unknown>);
        if (activeLocation.isPhantom) {
          // Phantom record must be in the store before project.sync() can persist it
          project.locationStore.add(activeLocation);
        }
        savedRecord = activeLocation;
      } else {
        // No prior location — create a fresh record in the store
        const added = project.locationStore.add(
          data as unknown as Record<string, unknown>
        ) as unknown as Location[];
        savedRecord = added[0];
      }

      // Link the entity record (e.g. Task, Resource, Client) to this location
      if (entityRecord) {
        entityRecord.set("locationId", savedRecord.id);
      }

      // Phase 1: persist the location (and entity link)
      await project.sync();

      // Phase 2: apply staged photo mutations now that we have a real numeric locationId
      if (hasPhotoChanges) {
        const filesToUpload = [...stagedFiles];
        const idsToRemove = [...photosToRemove];

        if (idsToRemove.length > 0) {
          await Promise.all(idsToRemove.map((id) => removePhoto(id)));
          setPhotosToRemove(new Set());
        }

        if (filesToUpload.length > 0) {
          await uploadPhotos(filesToUpload);
          setStagedFiles([]);
        } else {
          setStagedFiles([]);
        }
      }

      setActiveLocation(savedRecord);
      reset(toDefaults(savedRecord));

      toast.success("Location saved");
    } catch (err) {
      console.error("Error saving location:", err);
      toast.error("Something went wrong while saving. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = handleSubmit(onSubmit);

  // True when the user picked a saved location that isn't yet linked to this entity
  const isLinkingNewLocation =
    activeLocation !== null &&
    (entityRecord?.locationId ?? null) !== activeLocation.id;

  const hasPhotoChanges = stagedFiles.length > 0 || photosToRemove.size > 0;
  const canSave = isDirty || isLinkingNewLocation || hasPhotoChanges;

  const hideDestructiveActionsUntilPersisted =
    !!disableUnlinkUntilPersisted &&
    (!isPersistedLocation || isLinkingNewLocation);

  // ─── Close w/ unsaved changes confirmation ───────────────────────────────

  const handleClose = async () => {
    const hasUnsavedChanges = isDirty || isLinkingNewLocation || hasPhotoChanges;
    if (!hasUnsavedChanges) {
      onClose();
      return;
    }

    const confirmed = await confirm({
      title: "Unsaved Changes",
      description: "Your location edits will be lost if you close now.",
      alertDialogOverlay: { className: "z-[--swivu-bryntum-z-index]" },
      alertDialogContent: { className: "z-[--swivu-bryntum-z-index]" },
      customActions: ({ confirm, cancel }: { confirm: () => void; cancel: () => void }) => (
        <div className="flex w-full justify-end gap-2">
          <Button variant="ghost" onClick={cancel} size="sm">
            Cancel
          </Button>
          <Button variant="outline" onClick={confirm} size="sm">
            Don't Save
          </Button>
          <Button
            size="sm"
            disabled={isReadOnly}
            onClick={async () => {
              const ok = await trigger("streetAddress");
              if (!ok) {
                cancel();
                return;
              }
              handleSave();
              confirm();
            }}
          >
            Save Location
          </Button>
        </div>
      ),
    });

    if (confirmed) onClose();
  };

  // ─── Header ──────────────────────────────────────────────────────────────

  const headerRenderer = () => (
    <div className="drag-handle flex items-center gap-4 p-5 border-b border-border/50 rounded-t-lg">
      <h2 className="text-xl font-bold tracking-tight text-foreground">Location Settings</h2>
      <p className="text-sm text-muted-foreground">Configure address, site identity, work area, access &amp; guidance.</p>
      <div className="ml-auto flex items-center gap-3">
        <LocationReadinessBadge locationRecord={activeLocation ?? locationRecord} />
        {isReadOnly && (
          <span className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2 py-1 text-[11px] font-medium text-muted-foreground">
            Read-only
          </span>
        )}
        <ButtonGroup>
          {isPersistedLocation && !hideDestructiveActionsUntilPersisted && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 !bg-transparent hover:!bg-accent text-destructive hover:text-destructive disabled:text-destructive/40"
                  onClick={handleDelete}
                  disabled={isSaving || isReadOnly}
                >
                  <Trash2 className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete location</TooltipContent>
            </Tooltip>
          )}
          {isLinked && !hideDestructiveActionsUntilPersisted && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 !bg-transparent hover:!bg-accent"
                  onClick={handleUnlink}
                  disabled={isSaving || isReadOnly}
                >
                  <Link2Off className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Remove from task</TooltipContent>
            </Tooltip>
          )}
          <Button size="sm" onClick={handleSave} disabled={!canSave || isSaving || isReadOnly}>
            {isSaving ? <Loader2 className="size-4 animate-spin" /> : isLinkingNewLocation ? "Assign" : "Save"}
          </Button>
        </ButtonGroup>
      </div>
      <button
        onClick={handleClose}
        className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-6 -top-5 -right-5 bg-background p-3 rounded-full border opacity-100 !size-12"
      >
        <i className="fa-light fa-times text-swivu-icon" />
        <span className="sr-only">Close</span>
      </button>
    </div>
  );

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <BaseModal
      record={locationRecord ?? undefined}
      onClose={handleClose}
      title=""
      description=""
      editorInstance={editorInstance}
      hideMenu={true}
      size={{
        width: "66rem",
        height: "min(52rem, 90vh)",
        maxHeight: "90vh",
        maxWidth: "90vw",
        minWidth: "66rem",
        minHeight: "min(48rem, 90vh)",
      }}
      modal={true}
      version={undefined}
      swivuAssistanceConfig={undefined}
      headerRenderer={headerRenderer}
      footerRenderer={null}
      headerIconCls=""
      className={`bg-background resize-none overflow-visible ${editorInstance?.className || ""}`}
    >
      <BaseModalBody contentElementClassName="bg-transparent" className="!p-0">
        <form className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-6 space-y-6">

            {/* ── Section 1: Address ─────────────────────────────────────── */}
            <EditorSection>
              <EditorFormField label="Street Address" htmlFor="streetAddress" required error={errors.streetAddress?.message}>
                <AddressAutoComplete
                  value={streetAddressValue}
                  onChange={(val) => setValue("streetAddress", val, { shouldDirty: true })}
                  onSelectSavedLocation={handleSelectSavedLocation}
                  onSelectGooglePlace={handleSelectGooglePlace}
                  onSetManual={handleSetManual}
                  placeholder="Search or enter an address…"
                  disabled={isSaving || isReadOnly}
                  showClear={false}
                />
              </EditorFormField>

              <div className="grid grid-cols-4 gap-5">
                <EditorFormField label="City" htmlFor="city">
                  <Controller name="city" control={control} render={({ field }) => (
                    <Input id="city" variant="outline" placeholder="e.g. Springfield" autoComplete="off" disabled={isSaving || isReadOnly} {...field} className={ringClass("city")} />
                  )} />
                </EditorFormField>
                <EditorFormField label="State / Region" htmlFor="stateOrRegion">
                  <Controller name="stateOrRegion" control={control} render={({ field }) => (
                    <Input id="stateOrRegion" variant="outline" placeholder="e.g. IL" autoComplete="off" disabled={isSaving || isReadOnly} {...field} className={ringClass("stateOrRegion")} />
                  )} />
                </EditorFormField>
                <EditorFormField label="Postal Code" htmlFor="postalCode">
                  <Controller name="postalCode" control={control} render={({ field }) => (
                    <Input id="postalCode" variant="outline" placeholder="e.g. 62701" autoComplete="off" disabled={isSaving || isReadOnly} {...field} className={ringClass("postalCode")} />
                  )} />
                </EditorFormField>
                <EditorFormField label="Country" htmlFor="country">
                  <Controller name="country" control={control} render={({ field }) => (
                    <Input id="country" variant="outline" placeholder="e.g. United States" autoComplete="off" disabled={isSaving || isReadOnly} {...field} className={ringClass("country")} />
                  )} />
                </EditorFormField>
              </div>

              {/* latitude & longitude tracked in RHF state but not rendered */}
              <Controller name="latitude" control={control} render={({ field }) => <input type="hidden" {...field} value={field.value ?? ""} />} />
              <Controller name="longitude" control={control} render={({ field }) => <input type="hidden" {...field} value={field.value ?? ""} />} />
            </EditorSection>

            {/* ── Section 2: Site Identity ───────────────────────────────── */}
            <EditorSection withDivider>
              <div className="space-y-0.5">
                <p className="text-sm font-medium text-foreground">Site Identity</p>
                <p className="text-xs text-muted-foreground/60">Name the property and building to help field teams recognise the site.</p>
              </div>
              <div className="grid grid-cols-2 gap-5">
                <EditorFormField label="Property Name" htmlFor="propertyName">
                  <Controller name="propertyName" control={control} render={({ field }) => (
                    <Input id="propertyName" variant="outline" placeholder="e.g. Ashcroft Commons" autoComplete="off" disabled={isSaving || isReadOnly} {...field} className={ringClass("propertyName")} />
                  )} />
                </EditorFormField>
                <EditorFormField label="Building Name" htmlFor="buildingName">
                  <Controller name="buildingName" control={control} render={({ field }) => (
                    <Input id="buildingName" variant="outline" placeholder="e.g. Building B" autoComplete="off" disabled={isSaving || isReadOnly} {...field} className={ringClass("buildingName")} />
                  )} />
                </EditorFormField>
              </div>
            </EditorSection>

            {/* ── Section 3: Work Area ───────────────────────────────────── */}
            <EditorSection withDivider>
              <div className="space-y-0.5">
                <p className="text-sm font-medium text-foreground">Work Area</p>
                <p className="text-xs text-muted-foreground/60">Specify exactly where within the building the work takes place.</p>
              </div>
              <div className="grid grid-cols-3 gap-5">
                <EditorFormField label="Floor / Level" htmlFor="floorOrLevel">
                  <Controller name="floorOrLevel" control={control} render={({ field }) => (
                    <Input id="floorOrLevel" variant="outline" placeholder="e.g. 3rd Floor" autoComplete="off" disabled={isSaving || isReadOnly} {...field} className={ringClass("floorOrLevel")} />
                  )} />
                </EditorFormField>
                <EditorFormField label="Elevation" htmlFor="elevation">
                  <Controller name="elevation" control={control} render={({ field }) => (
                    <Input id="elevation" variant="outline" placeholder="e.g. North Elevation" autoComplete="off" disabled={isSaving || isReadOnly} {...field} className={ringClass("elevation")} />
                  )} />
                </EditorFormField>
                <EditorFormField label="Work Zone" htmlFor="workZoneName">
                  <Controller name="workZoneName" control={control} render={({ field }) => (
                    <Input id="workZoneName" variant="outline" placeholder="e.g. Parapet Return at NE Corner" autoComplete="off" disabled={isSaving || isReadOnly} {...field} className={ringClass("workZoneName")} />
                  )} />
                </EditorFormField>
              </div>
            </EditorSection>

            {/* ── Section 4: Access ─────────────────────────────────────── */}
            <EditorSection withDivider>
              <div className="space-y-0.5">
                <p className="text-sm font-medium text-foreground">Access</p>
                <p className="text-xs text-muted-foreground/60">Entry routes and staging details for the field team.</p>
              </div>
              <div className="grid grid-cols-2 gap-5">
                <EditorFormField label="Primary Entry Route" htmlFor="primaryEntryRoute">
                  <Controller name="primaryEntryRoute" control={control} render={({ field }) => (
                    <Textarea id="primaryEntryRoute" placeholder="e.g. Use rear service entrance off Maple Ave, ring buzzer #2" autoComplete="off" rows={2} disabled={isSaving || isReadOnly} {...field} className={cn("resize-y !text-base min-h-[4rem]", ringClass("primaryEntryRoute"))} />
                  )} />
                </EditorFormField>
                <EditorFormField label="Staging Area" htmlFor="stagingArea">
                  <Controller name="stagingArea" control={control} render={({ field }) => (
                    <Textarea id="stagingArea" placeholder="e.g. Rear lot near dumpster enclosure" autoComplete="off" rows={2} disabled={isSaving || isReadOnly} {...field} className={cn("resize-y !text-base min-h-[4rem]", ringClass("stagingArea"))} />
                  )} />
                </EditorFormField>
              </div>
            </EditorSection>

            {/* ── Section 5: Guidance ───────────────────────────────────── */}
            <EditorSection withDivider>
              <div className="space-y-0.5">
                <p className="text-sm font-medium text-foreground">Guidance</p>
                <p className="text-xs text-muted-foreground/60">Directions and notes that help field teams navigate on-site from the moment they arrive.</p>
              </div>
              <EditorFormField label="Start Here Note" htmlFor="startHereNote">
                <Controller name="startHereNote" control={control} render={({ field }) => (
                  <Textarea id="startHereNote" placeholder="e.g. Check in with front desk, ask for John" autoComplete="off" rows={3} disabled={isSaving || isReadOnly} {...field} className={cn("resize-y !text-base min-h-[4rem]", ringClass("startHereNote"))} />
                )} />
              </EditorFormField>
            </EditorSection>

            {/* ── Section 6: AI Summaries ───────────────────────────────── */}
            <EditorSection withDivider>
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-0.5 min-w-0">
                  <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
                    Summary
                    <Sparkles className="size-3.5 text-violet-400" />
                  </p>
                  <p className="text-xs text-muted-foreground/60">AI-generated overviews of this location for quick reference and field handoff.</p>
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={isSaving || isGeneratingSummary || isReadOnly || !selectedBoard?.id || !hasStreetAddress}
                  onClick={async () => {
                    if (!selectedBoard?.id) {
                      toast.error("No board selected.");
                      return;
                    }
                    if (isReadOnly) return;
                    setIsGeneratingSummary(true);
                    setDidJustGenerate(false);
                    try {
                      const v = getValues();
                      const payload = {
                        board: selectedBoard.id,
                        street_address: v.streetAddress || undefined,
                        city: v.city || undefined,
                        state_or_region: v.stateOrRegion || undefined,
                        postal_code: v.postalCode || undefined,
                        country: v.country || undefined,
                        latitude: v.latitude ?? undefined,
                        longitude: v.longitude ?? undefined,
                        property_name: v.propertyName || undefined,
                        building_name: v.buildingName || undefined,
                        floor_or_level: v.floorOrLevel || undefined,
                        elevation: v.elevation || undefined,
                        work_zone_name: v.workZoneName || undefined,
                        primary_entry_route: v.primaryEntryRoute || undefined,
                        staging_area: v.stagingArea || undefined,
                        start_here_note: v.startHereNote || undefined,
                      };

                      const resp = await generateLocationSummary(payload);
                      const summary = resp?.data?.location_summary ?? "";
                      const handoff = resp?.data?.workforce_handoff_note ?? "";

                      setValue("locationSummary", summary, { shouldDirty: true });
                      setValue("workforceHandoffNote", handoff, { shouldDirty: true });
                      flashHighlight(["locationSummary", "workforceHandoffNote"]);

                      setDidJustGenerate(true);
                      window.setTimeout(() => setDidJustGenerate(false), 900);
                    } catch (error) {
                      console.error(error);
                      reportError({
                        error,
                        provider: "openai",
                        fallback: "Failed to generate summary. Please try again.",
                      });
                    } finally {
                      setIsGeneratingSummary(false);
                    }
                  }}
                  className={cn(
                    "h-7 gap-1.5 px-2 text-xs text-violet-400 hover:text-violet-300 hover:bg-violet-500/10 disabled:opacity-40",
                    didJustGenerate && "text-emerald-400 hover:text-emerald-400 hover:bg-emerald-500/10"
                  )}
                >
                  {isGeneratingSummary ? (
                    <>
                      <Loader2 className="size-3.5 animate-spin" />
                      Generating…
                    </>
                  ) : didJustGenerate ? (
                    <>
                      <Check className="size-3.5" />
                      Generated
                    </>
                  ) : (
                    <>
                      <Sparkles className="size-3.5" />
                      Generate
                    </>
                  )}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-5">
                <AiTextareaField
                  name="locationSummary"
                  label="Location Summary"
                  placeholder="e.g. Building B · 3rd floor parapet, north elevation…"
                  control={control}
                  isSaving={isSaving}
                  ringClass={ringClass}
                />
                <AiTextareaField
                  name="workforceHandoffNote"
                  label="Workforce Handoff Note"
                  placeholder="e.g. Report to building mgmt on arrival…"
                  control={control}
                  isSaving={isSaving}
                  ringClass={ringClass}
                />
              </div>
            </EditorSection>

            {/* ── Section 7: Photos ─────────────────────────────────────── */}
            <LocationEditorPhotos
              locationRecord={activeLocation ?? locationRecord}
              boardId={boardId}
              stagedFiles={stagedFiles}
              photosToRemove={photosToRemove}
              onStageFiles={handleStageFiles}
              onRemoveStaged={handleRemoveStaged}
              onMarkForRemoval={handleMarkForRemoval}
              isSaving={isSaving}
              isReadOnly={isReadOnly}
            />

          </div>
        </form>
      </BaseModalBody>
    </BaseModal>
  );
};
